import { useState, useCallback, useEffect } from 'react'
import * as adminApi from '../services/adminApi.js'
import type { AdminUser, AdminMember, PaginatedResponse } from '../types/index.js'

export function useAdminUsers(initialPage = 1, limit = 20) {
  const [data, setData] = useState<PaginatedResponse<AdminUser> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(initialPage)

  const fetch = useCallback(async (p: number) => {
    setLoading(true)
    setError(null)
    try {
      const result = await adminApi.listUsers(p, limit)
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [limit])

  useEffect(() => {
    void fetch(page)
  }, [fetch, page])

  const updateUser = useCallback(
    async (id: string, payload: Partial<{ name: string; email: string; platformRole: 'user' | 'super_admin'; isActive: boolean }>) => {
      const updated = await adminApi.updateUser(id, payload)
      setData((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          data: prev.data.map((u) => (u.id === id ? updated : u)),
        }
      })
      return updated
    },
    [],
  )

  const resetPassword = useCallback(
    async (userId: string, payload: { password: string; confirm_password: string }) => {
      await adminApi.resetUserPassword(userId, payload)
    },
    [],
  )

  const addToWorkspace = useCallback(
    async (userId: string, payload: { workspaceId: string; role: 'owner' | 'admin' | 'member' }): Promise<AdminMember> => {
      return adminApi.addUserToWorkspace(userId, payload)
    },
    [],
  )

  const removeFromWorkspace = useCallback(
    async (userId: string, membershipId: string) => {
      await adminApi.removeUserFromWorkspace(userId, membershipId)
    },
    [],
  )

  const updateMembershipRole = useCallback(
    async (userId: string, membershipId: string, role: 'owner' | 'admin' | 'member'): Promise<AdminMember> => {
      return adminApi.updateUserMembershipRole(userId, membershipId, role)
    },
    [],
  )

  return {
    data,
    loading,
    error,
    page,
    setPage,
    refetch: () => fetch(page),
    updateUser,
    resetPassword,
    addToWorkspace,
    removeFromWorkspace,
    updateMembershipRole,
  }
}
